// @ts-nocheck
/**
 * Full-screen vertical reel, Instagram "saved reels" style: swipe up/down through a list of recipes as videos.
 * Used by the Likes tab (assets/vhp-likes-page.js), the search results (assets/vhp-search.js), Explore (assets/vhp-explore.js), the
 * collection pages' recipe grid (assets/vhp-collection-grid-reel.js) and the planner's
 * Rezepte tab and swap sheet (assets/vhp-planner-funnel.js).
 *  - Videos, slot and product id come from the recipe's swipe card (/products/{handle}?view=card).
 *  - "Rezept ansehen" opens the recipe sheet (assets/vhp-recipe-sheet.js) on top, or the recipe page without it.
 *  - The heart likes / unlikes through window.vhpPlannerLikes; it and "Einplanen" show only for plannable recipes
 *    (a meal with a slot and macros, data-plannable on the card): the likes and the planner need the slot.
 *  - Opening pushes a history entry (state.vhpReel), so the phone's back button closes it.
 *  - A modal <dialog>: it also opens over the planner's sheets; the recipe sheet and cook mode open on top of it.
 *  - Looks like the homepage reel (snippets/vhp-swipe-deck.liquid reel, assets/vhp-feed-reel.css): share next to the
 *    title, protein / kcal / time with icons, a row of glass buttons (the recipe as a round icon). Where the heart is
 *    a plain like (no onLike, primary or secondary: Explore, collections, search, Likes) it is the wide save button
 *    "In den Wochenplan"; the planner keeps a round heart next to its own buttons.
 *
 *   window.vhpReelViewer.open({
 *     items: [{ id, handle, title, image, protein, time, kcal,
 *               chips: ['1 Portion', …],     // optional: replaces the protein / time / kcal chips
 *               note: '', noteWarn: false }],  // optional line under the chips (the planner's "Tag danach")
 *     start: 0,                       // index of the first recipe
 *     label: 'Deine Likes als Videos', // dialog name
 *     title: 'Mittagessen',          // optional: shown above the count (Explore: the collection)
 *     source: 'likes',                // tracking
 *     planHref: (item) => '',         // optional "Einplanen" link per recipe
 *     onPlan: (item, index) => {},    // optional "Einplanen" button instead of the link
 *     primary: { label, onClick: (item, index) => {} }, // optional main button; "Rezept" moves next to it
 *                                     // per item: primary: false (no button), primaryLabel: '…'
 *     secondary: { label, onLabel, isOn: (item) => bool, onClick: (item, index) => {} }, // optional ghost toggle
 *                                     // before "Rezept" (the planner's "Nicht für mich"); repainted after onClick
 *     onRecipe: (item, index) => {},  // optional: opens the recipe itself (default: the recipe sheet)
 *     onLike: (item, on) => true,     // optional: likes through the caller (false: no change); isLiked: (item) => bool
 *     media: (item) => ({ src, poster, plannable, likable }), // optional: the video without fetching the swipe card
 *     onActive: (item, index, prev) => {}, // the recipe on screen changed
 *     host: element,                  // optional parent (the planner: its funnel, so its click handlers and toast work)
 *     onOpen: (dialog) => {},         // after opening (the planner moves its undo toast in)
 *     onClose: (item) => {},          // the recipe shown last
 *     originFor: (item) => element,   // optional: the recipe's tile; the reel zooms out of it and back into it
 *   })
 *   window.vhpReelViewer.hold(true / false) // pause / resume the visible video (a sheet on top, tab hidden)
 *   window.vhpReelViewer.collection(handle) // Promise of a collection's recipes as items (cached per page)
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpReelViewer) return;

  const SOUND_KEY = 'vhp_likes_reel_sound';
  const media = new Map(); // handle -> Promise<{ ok, src, poster, id, slot, plannable }>

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const likesApi = () => window.vhpPlannerLikes || null;
  const hrefFor = (item) => `/blogs/rezepte/${encodeURIComponent(item.handle)}`;

  const ICON = {
    back: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    muted: '<svg class="vhp-reel__muted" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>',
    sound: '<svg class="vhp-reel__sound" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>',
    play: '<svg viewBox="0 0 24 24" width="34" height="34" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.9l10.4-6.5a1 1 0 0 0 0-1.8L9.5 4.6A1 1 0 0 0 8 5.5z"/></svg>',
    plan: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15.5" rx="3"/><path d="M3.5 10h17M8 3v4M16 3v4M12 13v5M9.5 15.5h5"/></svg>',
    heart: '<svg viewBox="0 0 24 24" width="26" height="26" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.5s-8-4.7-8-10.6C4 7 6.1 5 8.6 5c1.5 0 2.7.7 3.4 1.8C12.7 5.7 13.9 5 15.4 5 17.9 5 20 7 20 9.9c0 5.9-8 10.6-8 10.6z"/></svg>',
    recipe: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 5h8M8 9h8M8 13h5"/><rect x="4.5" y="3.5" width="15" height="17" rx="2"/></svg>',
    share: '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 3 10 14"/><path d="m21 3-7 18-4-7-7-4 18-7Z"/></svg>',
  };

  // The recipe's video (and its meal slot, for the heart) come from its swipe card, the source the feed uses.
  const cardFor = (handle) => {
    if (!media.has(handle)) {
      const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=card`;
      media.set(
        handle,
        fetch(url, { headers: { Accept: 'text/html' } })
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.recipe-card');
            const source =
              card?.querySelector('template')?.content.querySelector('video source[src]') || card?.querySelector('video source[src]');
            return {
              ok: Boolean(card),
              src: source?.getAttribute('src') || '',
              poster: card?.dataset.poster || '',
              id: card?.dataset.id || '',
              slot: card?.dataset.slot || '',
              plannable: card?.dataset.plannable === 'true',
            };
          })
          .catch(() => ({ ok: false, src: '', poster: '', id: '', slot: '', plannable: false }))
      );
    }
    return media.get(handle);
  };

  const isLiked = (item) => {
    if (!item?.id) return false;
    if (opts?.isLiked) return Boolean(opts.isLiked(item));
    const api = likesApi();
    if (!api) return false;
    const key = api.productId ? api.productId(item.id) : String(item.id);
    return api.read().likes.items.some((row) => row.id === key);
  };

  // The caller's own media (the planner has its cards in memory), else the recipe's swipe card.
  const mediaFor = (item) => {
    if (opts?.media) {
      const own = opts.media(item) || {};
      return Promise.resolve({ ok: true, id: item.id || '', slot: item.slot || '', src: '', poster: '', plannable: false, ...own });
    }
    return item.handle ? cardFor(item.handle) : Promise.resolve({ ok: false, src: '', poster: '', id: '', slot: '', plannable: false });
  };

  /* ---------------------------------------------------------------- frame */

  let reel = null;
  let track = null;
  let count = null;
  let heading = null;
  let muteBtn = null;
  let opts = null;
  let items = [];
  let active = -1;
  let observer = null;
  let held = false;
  let returnFocus = null;
  let muted = true;
  try {
    muted = sessionStorage.getItem(SOUND_KEY) !== 'on';
  } catch (_) {}

  const isOpen = () => Boolean(reel?.open);

  const build = () => {
    if (reel) return;
    reel = document.createElement('dialog');
    reel.className = 'vhp-reel';
    reel.innerHTML = `<div class="vhp-reel__track" data-reel-track></div>
      <div class="vhp-reel__top">
        <button type="button" class="vhp-reel__icon" data-reel-close aria-label="${tr('common.close')}">${ICON.back}</button>
        <span class="vhp-reel__where"><span class="vhp-reel__heading" data-reel-heading hidden></span><span class="vhp-reel__count" data-reel-count aria-live="polite"></span></span>
        <button type="button" class="vhp-reel__icon vhp-reel__icon--glass" data-reel-mute aria-pressed="true" aria-label="${tr('reel.sound_on')}">${ICON.muted}${ICON.sound}</button>
      </div>
      <p class="vhp-reel__toast" data-reel-toast role="status" hidden></p>`;
    document.body.appendChild(reel);
    track = reel.querySelector('[data-reel-track]');
    count = reel.querySelector('[data-reel-count]');
    heading = reel.querySelector('[data-reel-heading]');
    muteBtn = reel.querySelector('[data-reel-mute]');
    reel.addEventListener('click', onClick);
    // Escape reaches only the top dialog: a recipe sheet or cook mode on top closes first.
    reel.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    reel.addEventListener('keydown', onKey);
  };

  // Protein, kcal and time as the homepage reel shows them: icon, bold value, light label (no pills). The planner's
  // own lines (item.chips: portion, its kcal and protein) as plain facts.
  const factsHtml = (item) => {
    if (Array.isArray(item.chips)) return item.chips.map((chip) => `<span class="vhp-reel__fact">${esc(chip)}</span>`).join('');
    const protein = Number(item.protein);
    const kcal = Number(item.kcal);
    const time = Number(item.time);
    return [
      protein > 0 ? `<span class="vhp-reel__fact vhp-reel__fact--protein"><strong>${Math.round(protein)}g</strong> Protein</span>` : '',
      kcal > 0 ? `<span class="vhp-reel__fact vhp-reel__fact--kcal"><strong>${Math.round(kcal)}</strong> kcal</span>` : '',
      time > 0 ? `<span class="vhp-reel__fact vhp-reel__fact--time"><strong>${Math.round(time)}</strong> ${esc(tr('common.min', { n: '' }).trim())}</span>` : '',
    ].join('');
  };

  // The heart as the homepage reel's wide save button ("In den Wochenplan" / "Im Wochenplan ✓"): where it is a plain
  // like. The planner (its own onLike, main or ghost buttons) keeps the round heart.
  const wideLike = () => !opts.onLike && !opts.primary && !opts.secondary;
  const paintLike = (like, on) => {
    if (!like) return;
    like.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (!like.classList.contains('vhp-reel__cta')) return;
    like.classList.toggle('is-on', on);
    const label = like.querySelector('.vhp-reel__cta-label');
    if (label) label.textContent = on ? tr('reels.cta_saved') : tr('reels.cta_cook');
  };

  // Only the slides around the visible one have content (poster, video, blurred chips); the rest are empty
  // full-height shells that keep the scroll-snap positions. iOS Safari kills the page ("problem repeatedly
  // occurred") when a long reel builds every slide, and when videos scrolled past keep their decoder.
  const KEEP = 2; // slides with content on each side of the visible one
  const VIDEO_KEEP = 1; // of those, slides with a loaded video

  const secondaryOn = (item) => {
    try {
      return Boolean(opts?.secondary?.isOn?.(item));
    } catch (_) {
      return false;
    }
  };
  const secondaryLabel = (item) => (secondaryOn(item) && opts.secondary.onLabel) || opts.secondary.label || '';
  const paintSecondary = (slide, item) => {
    const btn = slide?.querySelector('[data-reel-secondary]');
    if (!btn || !opts?.secondary) return;
    btn.setAttribute('aria-pressed', secondaryOn(item) ? 'true' : 'false');
    btn.textContent = secondaryLabel(item);
  };

  const shellHtml = (item, index) =>
    `<section class="vhp-reel__slide" data-index="${index}" aria-label="${esc(item.title)}"></section>`;

  const slideHtml = (item) => {
    const plan = opts.onPlan
      ? `<button type="button" class="vhp-reel__btn vhp-reel__glass" data-reel-plan aria-label="Einplanen" hidden>${ICON.plan}</button>`
      : opts.planHref && opts.planHref(item)
        ? `<a class="vhp-reel__btn vhp-reel__glass" href="${esc(opts.planHref(item))}" data-like-plan data-reel-plan aria-label="Einplanen" hidden>${ICON.plan}</a>`
        : '';
    // Per recipe: primary false leaves it out (the dish already in the plan), primaryLabel replaces the label.
    const primaryLabel = opts.primary && item.primary !== false ? item.primaryLabel || opts.primary.label : '';
    const primary = primaryLabel ? `<button type="button" class="vhp-reel__main vhp-reel__glass" data-reel-primary>${esc(primaryLabel)}</button>` : '';
    // A ghost toggle (the planner's "Nicht für mich"): isOn(item) picks the label.
    const secondary = opts.secondary
      ? `<button type="button" class="vhp-reel__main vhp-reel__main--ghost vhp-reel__glass vhp-reel__secondary" data-reel-secondary aria-pressed="${secondaryOn(item)}">${esc(secondaryLabel(item))}</button>`
      : '';
    // The recipe: a round glass icon; alone in its row (no save button) it shows its label. An own recipe has no
    // page: a button the caller opens (onRecipe).
    const recipeInner = `${ICON.recipe}<span class="vhp-reel__recipe-label">${tr('common.view_recipe')}</span>`;
    const recipe = item.handle
      ? `<a class="vhp-reel__btn vhp-reel__glass vhp-reel__recipe" href="${esc(hrefFor(item))}" data-reel-recipe aria-label="${tr('common.view_recipe')}">${recipeInner}</a>`
      : `<button type="button" class="vhp-reel__btn vhp-reel__glass vhp-reel__recipe" data-reel-recipe aria-label="${tr('common.view_recipe')}">${recipeInner}</button>`;
    const liked = isLiked(item);
    // The wide save button shows from the start (most recipes can be saved) and hides once the card says otherwise
    // (loadSlide); the round heart waits for the card.
    const like = wideLike()
      ? `<button type="button" class="vhp-reel__cta vhp-reel__glass${liked ? ' is-on' : ''}" data-reel-like aria-pressed="${liked}"><span class="vhp-reel__cta-orb" aria-hidden="true">${ICON.heart}</span><span class="vhp-reel__cta-label">${liked ? tr('reels.cta_saved') : tr('reels.cta_cook')}</span></button>`
      : `<button type="button" class="vhp-reel__btn vhp-reel__glass vhp-reel__like" data-reel-like aria-pressed="${liked}" aria-label="${tr('reel.like')}" hidden>${ICON.heart}</button>`;
    const share = item.handle ? `<button type="button" class="vhp-reel__share" data-reel-share aria-label="${tr('reel.share')}">${ICON.share}</button>` : '';
    const note = item.note ? `<p class="vhp-reel__note${item.noteWarn ? ' is-warn' : ''}">${esc(item.note)}</p>` : '';
    return `${item.image ? `<img class="vhp-reel__poster" src="${esc(item.image)}" alt="" decoding="async">` : ''}
      <video class="vhp-reel__video" playsinline loop muted preload="none"></video>
      <span class="vhp-reel__loading" aria-hidden="true"></span>
      <span class="vhp-reel__paused" aria-hidden="true">${ICON.play}</span>
      <span class="vhp-reel__scrim" aria-hidden="true"></span>
      <div class="vhp-reel__info">
        <div class="vhp-reel__head">
          <h2 class="vhp-reel__title">${esc(item.title)}</h2>
          ${share}
        </div>
        <div class="vhp-reel__facts">${factsHtml(item)}</div>
        ${note}
        <div class="vhp-reel__actions">
          ${recipe}${plan}${secondary}${primary}${like}
        </div>
      </div>`;
  };

  const slideAt = (index) => track.children[index] || null;

  const paintMute = () => {
    muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    muteBtn.setAttribute('aria-label', muted ? tr('reel.sound_on') : tr('reel.sound_off'));
  };

  const playSlide = (slide) => {
    const video = slide?.querySelector('video');
    if (!video || !video.src || held) return;
    video.muted = muted;
    slide.classList.remove('is-paused');
    video.play().catch(() => {
      // Autoplay with sound blocked: fall back to muted, the speaker button turns it on.
      if (!video.muted) {
        muted = true;
        video.muted = true;
        paintMute();
        video.play().catch(() => slide.classList.add('is-paused'));
      } else slide.classList.add('is-paused');
    });
  };

  // Stops the download and hands the decoder back (removing the element alone does not on iOS).
  const releaseVideo = (video) => {
    if (!video) return;
    video.pause();
    if (video.hasAttribute('src')) {
      video.removeAttribute('src');
      try {
        video.load();
      } catch (_) {}
    }
  };

  const fillSlide = (index) => {
    const slide = slideAt(index);
    if (!slide || slide.dataset.filled) return;
    slide.dataset.filled = '1';
    slide.innerHTML = slideHtml(items[index]);
  };

  const emptySlide = (slide) => {
    if (!slide.dataset.filled) return;
    releaseVideo(slide.querySelector('video'));
    slide.innerHTML = '';
    slide.className = 'vhp-reel__slide';
    delete slide.dataset.filled;
    delete slide.dataset.loaded;
  };

  const loadSlide = (index) => {
    const slide = slideAt(index);
    const item = items[index];
    if (!slide || !item || !slide.dataset.filled || slide.dataset.loaded) return;
    slide.dataset.loaded = '1';
    mediaFor(item).then((card) => {
      if (!slide.isConnected || !slide.dataset.loaded) return;
      // Search results carry no meal slot: the card has it. Card not loadable: trust the item (a like has a slot).
      if (!item.slot && card.slot) item.slot = card.slot;
      if (!item.id && card.id) item.id = card.id;
      const plannable = card.ok ? card.plannable : Boolean(item.slot);
      const likable = card.likable ?? plannable;
      const like = slide.querySelector('[data-reel-like]');
      like.hidden = !((opts?.onLike || likesApi()) && item.id && (likable || isLiked(item)));
      paintLike(like, isLiked(item));
      // No save button: the recipe takes the row with its label.
      slide.classList.toggle('is-solo', like.hidden && !slide.querySelector('[data-reel-primary], [data-reel-secondary]'));
      const plan = slide.querySelector('[data-reel-plan]');
      if (plan) plan.hidden = !plannable;

      const video = slide.querySelector('video');
      if (!card.src || !video) {
        slide.classList.add('is-novideo');
        return;
      }
      if (card.poster) video.poster = card.poster;
      video.preload = index === active ? 'auto' : 'metadata';
      video.src = card.src;
      video.addEventListener('playing', () => slide.classList.add('is-playing'), { once: true });
      if (index === active) playSlide(slide);
    });
  };

  const activate = (index) => {
    if (index === active) return;
    const prev = active;
    active = index;
    count.textContent = `${index + 1} / ${items.length}`;
    Array.from(track.children).forEach((slide, i) => {
      const far = Math.abs(i - index);
      if (far > KEEP) emptySlide(slide);
      else if (far > VIDEO_KEEP && slide.dataset.loaded) {
        releaseVideo(slide.querySelector('video'));
        slide.classList.remove('is-playing', 'is-novideo');
        delete slide.dataset.loaded;
      } else if (i !== index) slide.querySelector('video')?.pause();
    });
    for (let i = index - KEEP; i <= index + KEEP; i += 1) fillSlide(i);
    [index, index + 1, index - 1].forEach(loadSlide);
    playSlide(slideAt(index));
    // The next recipes' sheets load while this one plays, so "Rezept ansehen" opens at once.
    if (!opts.onRecipe && items[index].handle) window.vhpRecipeSheet?.prefetch?.(hrefFor(items[index]));
    opts.onActive?.(items[index], index, prev);
  };

  const step = (dir) => {
    if (active < 0) return;
    const reduce = reduceMotion();
    track.scrollTo({ top: (active + dir) * track.clientHeight, behavior: reduce ? 'auto' : 'smooth' });
  };

  // Keyboard: arrow / page keys move one recipe.
  function onKey(event) {
    if (event.target.closest?.('input, textarea, select')) return;
    const down = event.key === 'ArrowDown' || event.key === 'PageDown';
    const up = event.key === 'ArrowUp' || event.key === 'PageUp';
    if (!down && !up) return;
    event.preventDefault();
    step(down ? 1 : -1);
  }

  /* ---------------------------------------------------------------- zoom (tile <-> reel) */

  const ZOOM_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'; // --app-ease
  let closing = null; // { animation, finish } while the reel shrinks back into its tile

  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const originOf = (item) => {
    try {
      return (item && opts?.originFor?.(item)) || null;
    } catch (_) {
      return null;
    }
  };

  // Keyframes from the tile to the full reel: the reel scaled down to cover the tile, clipped to the tile's box
  // and corner radius (transform-origin 0 0, see the CSS). Null when the tile is off screen or motion is reduced.
  const zoomFrames = (tile) => {
    if (!tile || !reel.animate || reduceMotion()) return null;
    const t = tile.getBoundingClientRect();
    const r = reel.getBoundingClientRect();
    if (!t.width || !t.height || !r.width || t.bottom <= 0 || t.top >= window.innerHeight) return null;
    const s = Math.max(t.width / r.width, t.height / r.height);
    const ix = (r.width - t.width / s) / 2;
    const iy = (r.height - t.height / s) / 2;
    const radius = (parseFloat(getComputedStyle(tile).borderTopLeftRadius) || 0) / s;
    return [
      {
        transform: `translate(${t.left - r.left - ix * s}px, ${t.top - r.top - iy * s}px) scale(${s})`,
        clipPath: `inset(${iy}px ${ix}px round ${radius}px)`,
      },
      { transform: 'none', clipPath: 'inset(0px 0px round 0px)' },
    ];
  };

  // Title, buttons and scrim fade in after the photo has grown (and out before it shrinks).
  const chrome = () => reel.querySelectorAll('.vhp-reel__top, .vhp-reel__scrim, .vhp-reel__info, .vhp-reel__loading');

  /* ---------------------------------------------------------------- open / close */

  const open = (options) => {
    const list = (options?.items || []).filter((item) => item && (item.handle || item.id));
    if (!list.length) return;
    build();
    if (closing) closing.finish();
    const focus = isOpen() ? returnFocus : document.activeElement;
    if (isOpen()) closeUi(true);
    opts = options;
    items = list.map((item) => ({ ...item }));
    const start = Math.max(0, Math.min(items.length - 1, Number(options.start) || 0));
    active = -1;
    held = false;
    returnFocus = focus;
    reel.setAttribute('aria-label', options.label || tr('reel.videos'));
    heading.textContent = options.title || '';
    heading.hidden = !options.title;
    track.innerHTML = items.map(shellHtml).join('');
    // Content first, so the zoom fades the title and buttons in with it.
    for (let i = start - KEEP; i <= start + KEEP; i += 1) fillSlide(i);
    const host = options.host || document.body;
    if (reel.parentNode !== host) host.appendChild(reel);
    reel.showModal();
    reel.classList.remove('is-in');
    const frames = zoomFrames(originOf(items[start]));
    if (frames) {
      reel.animate(frames, { duration: 420, easing: ZOOM_EASE });
      chrome().forEach((el) => el.animate({ opacity: [0, 1] }, { duration: 220, delay: 240, fill: 'backwards' }));
    } else {
      reel.offsetHeight; // restart the entrance
      reel.classList.add('is-in');
    }
    document.documentElement.classList.add('vhp-reel-open');
    track.scrollTop = start * track.clientHeight;
    paintMute();
    activate(start);
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate(Number(entry.target.dataset.index));
        });
      },
      { root: track, threshold: 0.6 }
    );
    Array.from(track.children).forEach((slide) => observer.observe(slide));
    // Re-opened while open: the reel keeps its one history entry.
    if (!history.state?.vhpReel) {
      try {
        history.pushState({ ...(history.state || {}), vhpReel: 1 }, '', location.href);
      } catch (_) {}
    }
    reel.querySelector('[data-reel-close]').focus({ preventScroll: true });
    options.onOpen?.(reel);
    window.vhpTrack?.('likes_reel_open', { start: items[start].handle || items[start].id, size: items.length, source: options.source || '' });
  };

  // instant: no zoom back (re-open while open)
  const closeUi = (instant) => {
    if (!isOpen() || closing) return;
    const last = items[active];
    observer?.disconnect();
    observer = null;

    let fades = [];
    const finish = () => {
      if (closing) closing.animation.cancel();
      fades.forEach((fade) => fade.cancel());
      closing = null;
      reel.style.pointerEvents = '';
      track.querySelectorAll('video').forEach(releaseVideo);
      track.innerHTML = ''; // drops the slides
      reel.close();
      active = -1;
      document.documentElement.classList.remove('vhp-reel-open');
      const done = opts?.onClose;
      opts = null;
      if (last && done) done(last);
      const focus = returnFocus;
      returnFocus = null;
      if (!instant && focus?.isConnected) focus.focus({ preventScroll: true });
    };

    // Shrink back into the tile of the recipe shown last (scrolled into view first, it may be further down).
    const tile = instant ? null : originOf(last);
    tile?.scrollIntoView({ block: 'nearest' });
    const frames = tile && zoomFrames(tile);
    if (!frames) {
      finish();
      return;
    }
    fades = Array.from(chrome(), (el) => el.animate({ opacity: [1, 0] }, { duration: 120, fill: 'forwards' }));
    reel.style.pointerEvents = 'none';
    const animation = reel.animate(frames.reverse(), { duration: 360, easing: ZOOM_EASE, fill: 'forwards' });
    closing = { animation, finish };
    animation.onfinish = () => closing?.animation === animation && finish();
  };

  const close = () => {
    if (history.state?.vhpReel) history.back();
    else closeUi();
  };

  // A sheet on top, or the tab hidden: the visible video waits.
  // Share as the homepage reel does (assets/vhp-collection-reels.js shareCurrent): the phone's share sheet, else the
  // link copied, with a short note.
  let toastTimer = 0;
  const showToast = (text) => {
    const toast = reel?.querySelector('[data-reel-toast]');
    if (!toast) return;
    toast.textContent = text;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (toast.hidden = true), 2200);
  };
  const share = async (item) => {
    const url = new URL(hrefFor(item), location.origin).href;
    const text = item.title ? tr('reels.share_text', { title: item.title }) : tr('reels.share_text_plain');
    window.vhpTrack?.('recipe_share', { recipe_url: url, method: navigator.share ? 'native' : 'copy', surface: 'viewer', source: opts?.source || '' });
    if (navigator.share) {
      try {
        await navigator.share({ title: item.title, text, url });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast(tr('common.link_copied'));
      return;
    } catch (_) {}
    // Old way: a hidden field in the dialog (outside it the modal makes it unselectable).
    try {
      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;left:-9999px';
      reel.appendChild(field);
      field.select();
      const ok = document.execCommand('copy');
      field.remove();
      showToast(ok ? tr('common.link_copied') : tr('reels.share_failed'));
    } catch (_) {
      showToast(tr('reels.share_failed'));
    }
  };

  const hold = (on) => {
    held = Boolean(on);
    if (!isOpen()) return;
    const slide = slideAt(active);
    if (held) slide?.querySelector('video')?.pause();
    else playSlide(slide);
  };

  window.addEventListener('popstate', () => {
    if (!isOpen()) return;
    if (!history.state?.vhpReel) closeUi();
    else if (!history.state?.vhpLayer) playSlide(slideAt(active)); // back from the recipe sheet
  });

  // Instagram's in-app browser keeps playing hidden media unless it is stopped.
  document.addEventListener('visibilitychange', () => {
    if (!isOpen()) return;
    if (document.visibilityState === 'hidden') slideAt(active)?.querySelector('video')?.pause();
    else if (!history.state?.vhpLayer) playSlide(slideAt(active));
  });

  // Reloaded while the reel was open: the reel is gone, so drop its history marker.
  if (history.state?.vhpReel) {
    try {
      const { vhpReel, ...rest } = history.state;
      history.replaceState(Object.keys(rest).length ? rest : null, '');
    } catch (_) {}
  }

  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-reel-close]')) {
      close();
      return;
    }

    if (target.closest('[data-reel-mute]')) {
      muted = !muted;
      try {
        sessionStorage.setItem(SOUND_KEY, muted ? 'off' : 'on');
      } catch (_) {}
      paintMute();
      const video = slideAt(active)?.querySelector('video');
      if (video) video.muted = muted;
      return;
    }

    const index = Number(target.closest('.vhp-reel__slide')?.dataset.index);
    const item = items[index];

    if (target.closest('[data-reel-primary]')) {
      if (item) opts.primary.onClick(item, index);
      return;
    }

    if (target.closest('[data-reel-secondary]')) {
      if (item && opts.secondary) {
        opts.secondary.onClick(item, index);
        paintSecondary(slideAt(index), item);
      }
      return;
    }

    const recipe = target.closest('[data-reel-recipe]');
    if (recipe) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (opts.onRecipe) {
        event.preventDefault();
        slideAt(active)?.querySelector('video')?.pause();
        if (item) opts.onRecipe(item, index);
        return;
      }
      if (!window.vhpRecipeSheet || !recipe.hasAttribute('href')) return;
      event.preventDefault();
      slideAt(active)?.querySelector('video')?.pause();
      window.vhpRecipeSheet.open(recipe.getAttribute('href'));
      return;
    }

    if (opts.onPlan && target.closest('[data-reel-plan]')) {
      if (item) opts.onPlan(item, index);
      return;
    }

    const like = target.closest('[data-reel-like]');
    if (like) {
      if (!item) return;
      const on = like.getAttribute('aria-pressed') !== 'true';
      // Saving costs the newsletter signup once (assets/vhp-mail-signup.js gate, Maxim 2026-11-03); the same tap again after.
      if (on && window.vhpMailSignup?.gate({ recipe: item.id, at: opts?.source || 'viewer', onSaved: () => like.click() })) return;
      if (opts.onLike) {
        if (opts.onLike(item, on) === false) return;
      } else {
        const api = likesApi();
        if (!api) return;
        if (on) {
          // A like from the Likes tab keeps its timestamp, so it goes back where it was.
          if (!api.addLike({ ...item, at: item.at || new Date().toISOString() })) return;
        } else api.removeLike(item.id);
      }
      if (on) window.vhpTrack?.('reel_like', { recipe_url: item.handle ? hrefFor(item) : '', slot: item.slot, source: opts?.source || '' });
      paintLike(like, on);
      return;
    }

    if (target.closest('[data-reel-share]')) {
      if (item) share(item);
      return;
    }

    // Tap on the video: pause / play.
    const slide = target.closest('.vhp-reel__slide');
    const video = slide?.querySelector('video');
    if (!video || !video.src || index !== active) return;
    if (video.paused) playSlide(slide);
    else {
      video.pause();
      slide.classList.add('is-paused');
    }
  }

  // A collection's recipes as reel items, in its own order (templates/collection.reel-index.liquid): no search
  // records needed. Explore's Neu / Viral rows and the collection page's grid play these.
  const indexCache = new Map();
  const collectionItems = (handle) => {
    if (!indexCache.has(handle)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const request = (async () => {
        const list = [];
        for (let page = 1; page; ) {
          const response = await fetch(`${root}collections/${encodeURIComponent(handle)}?view=reel-index&page=${page}`, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`reel index ${response.status}`);
          const holder = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('#vhpReelIndex');
          if (!holder) throw new Error('reel index missing');
          list.push(...JSON.parse(holder.textContent || '[]'));
          page = Number(holder.dataset.nextPage) || 0;
        }
        return list;
      })();
      indexCache.set(handle, request);
      request.catch(() => indexCache.delete(handle));
    }
    return indexCache.get(handle);
  };

  window.vhpReelViewer = {
    open,
    close,
    isOpen,
    hold,
    next: () => step(1),
    prefetch: (handle) => handle && cardFor(handle),
    collection: collectionItems,
  };
})();
